// ---- Combat crowd-control + AoE shape VFX (world-anchored bursts) ----------
// Snare / stun / knockback onset effects + special-move sector/line telegraphs.
// Spawned from server `combatFx` / `aoe` near-broadcasts. Short-lived THREE
// meshes on the shared toon mats — no post-chain work. Status icons under HP
// bars live in playerbars / npc bars; this module is the burst-only half.

import * as THREE from "three";
import { toonMat } from "./toon.ts";
import { isFullCircle } from "./sim/aoe.ts";

export type CombatFxKind = "snare" | "stun" | "slow" | "knockback";

export type AoeFxSpec =
  | { kind: "sector"; arcDeg: number; range: number; ry: number }
  | { kind: "line"; length: number; width: number; ry: number };

type Burst = {
  root: THREE.Group;
  kind: CombatFxKind | "aoe";
  age: number;
  life: number;
};

let scene: THREE.Scene | null = null;
const live: Burst[] = [];
const pool: THREE.Group[] = [];

const LIFE: Record<CombatFxKind | "aoe", number> = {
  snare: 0.7,
  stun: 0.85,
  slow: 0.75,
  knockback: 0.45,
  // Slightly longer so the thick outline + fill decal can read.
  aoe: 0.45,
};

export function initCombatFx(s: THREE.Scene): void {
  scene = s;
}

export function clearCombatFx(): void {
  for (let i = live.length - 1; i >= 0; i--) recycle(i);
}

function takeRoot(): THREE.Group {
  return pool.pop() ?? new THREE.Group();
}

function recycle(i: number): void {
  const b = live[i]!;
  b.root.removeFromParent();
  while (b.root.children.length) {
    const c = b.root.children[0]!;
    b.root.remove(c);
    // Geos are unique per burst — dispose to avoid leaks. Materials are either
    // the shared toonMat cache (snare/stun/knock) or owned MeshBasic mats (AoE).
    if ((c as THREE.Mesh).isMesh) {
      const m = c as THREE.Mesh;
      m.geometry?.dispose();
      const mat = m.material as THREE.Material | THREE.Material[];
      for (const one of Array.isArray(mat) ? mat : [mat]) {
        if (one?.userData?.owned) one.dispose();
      }
    }
  }
  pool.push(b.root);
  live.splice(i, 1);
}

/** Spawn a world-space onset VFX at (x,y,z). */
export function spawnCombatFx(kind: CombatFxKind, x: number, y: number, z: number): void {
  if (!scene) return;
  const root = takeRoot();
  root.position.set(x, y, z);
  root.scale.set(1, 1, 1);
  root.clear();

  if (kind === "snare") buildSnare(root);
  else if (kind === "stun") buildStun(root);
  else if (kind === "slow") buildSlow(root);
  else buildKnock(root);

  scene.add(root);
  live.push({ root, kind, age: 0, life: LIFE[kind] });
}

/** Special-move volume flash at caster feet (sector wedge / pierce corridor). */
export function spawnAoeFx(x: number, y: number, z: number, spec: AoeFxSpec): void {
  if (!scene) return;
  const root = takeRoot();
  root.position.set(x, y + 0.05, z);
  root.rotation.set(0, 0, 0);
  root.scale.set(1, 1, 1);
  root.clear();

  if (spec.kind === "sector") buildSectorAoe(root, spec.arcDeg, spec.range, spec.ry);
  else buildLineAoe(root, spec.length, spec.width, spec.ry);

  scene.add(root);
  live.push({ root, kind: "aoe", age: 0, life: LIFE.aoe });
}

// ---- AoE ground decals: opaque fill + thick rounded outline ----------------
// Graphic flat markers (MeshBasic — lighting-proof) so specials read as bold
// UI-on-the-ground: solid fill, fat stroke, rounded corners. Shape space is
// XY → rotated to XZ (y→z); local +y in the shape = world +z = facing.

/** Stroke width of the outline tube (world units). */
const AOE_OUTLINE = 0.022;
/** Outer-corner fillet radius for cones / corridors. */
const AOE_CORNER = 0.22;
/** Ground lift so fill/outline sit above terrain without z-fight. */
const AOE_Y = 0.04;

/** Unlit decal material (disposed with the burst). Fill uses low opacity so
 *  the ground reads through; outline stays solid. */
function aoeMat(color: number, opacity = 1): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({
    color,
    transparent: opacity < 1,
    opacity,
    // Transparent fills skip depthWrite so stacked terrain doesn't punch holes.
    depthWrite: opacity >= 1,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
  m.userData.owned = true;
  return m;
}

/** Fill opacity — translucent so the volume reads as a wash, not a solid blot. */
const AOE_FILL_OPACITY = 0.3;

/** Shape XY → ground XZ (y-up world). Shape +Y becomes world +Z (facing
 *  forward at ry=0 — same as forwardXZ). rotateX(+π/2) maps (x,y,0)→(x,0,y);
 *  the old −π/2 mirrored the cone into −Z (behind the caster). */
function layFlat(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  geo.rotateX(Math.PI / 2);
  return geo;
}

/**
 * Sector / full-circle outline in shape space (x = world X, y = world Z).
 * Outer corners are filleted; the apex gets a small round cap so cones don't
 * come to a needle tip.
 */
function sectorShape(arcDeg: number, range: number, cornerR: number): THREE.Shape {
  const shape = new THREE.Shape();
  if (isFullCircle(arcDeg)) {
    shape.absarc(0, 0, range, 0, Math.PI * 2, false);
    return shape;
  }
  const half = (arcDeg * Math.PI) / 360;
  const R = Math.max(range, 0.2);
  const cr = Math.min(cornerR, R * 0.22, R * half * 0.85);
  const apexR = Math.min(cr * 0.55, R * 0.1, 0.16);
  // Angular inset so the outer arc meets the outer fillets cleanly.
  const dA = Math.min(cr / R, half * 0.45);
  const a0 = -half;
  const a1 = half;
  const outer0 = a0 + dA;
  const outer1 = a1 - dA;
  const radialOut = Math.max(R - cr, apexR + 0.02);

  // Left radial out from the apex cap.
  shape.moveTo(Math.sin(a0) * apexR, Math.cos(a0) * apexR);
  shape.lineTo(Math.sin(a0) * radialOut, Math.cos(a0) * radialOut);
  // Outer-left rounded corner (control point at the sharp tip).
  shape.quadraticCurveTo(
    Math.sin(a0) * R, Math.cos(a0) * R,
    Math.sin(outer0) * R, Math.cos(outer0) * R,
  );
  // Outer arc (a=0 faces +Y in shape space = +Z world). Sample densely.
  const arcSegs = Math.max(10, Math.ceil(arcDeg / 6));
  for (let i = 1; i <= arcSegs; i++) {
    const a = outer0 + (i / arcSegs) * (outer1 - outer0);
    shape.lineTo(Math.sin(a) * R, Math.cos(a) * R);
  }
  // Outer-right rounded corner.
  shape.quadraticCurveTo(
    Math.sin(a1) * R, Math.cos(a1) * R,
    Math.sin(a1) * radialOut, Math.cos(a1) * radialOut,
  );
  // Right radial back in + rounded apex cap.
  shape.lineTo(Math.sin(a1) * apexR, Math.cos(a1) * apexR);
  const apexSegs = Math.max(4, Math.ceil((a1 - a0) / (Math.PI / 12)));
  for (let i = 1; i <= apexSegs; i++) {
    const a = a1 - (i / apexSegs) * (a1 - a0);
    shape.lineTo(Math.sin(a) * apexR, Math.cos(a) * apexR);
  }
  shape.closePath();
  return shape;
}

/** Rounded rectangle corridor: caster at y=0, tip at y=length, width on X. */
function lineShape(length: number, width: number, cornerR: number): THREE.Shape {
  const L = Math.max(length, 0.2);
  const hw = Math.max(width, 0.2) * 0.5;
  const r = Math.min(cornerR, hw - 0.01, L * 0.5 - 0.01);
  const shape = new THREE.Shape();
  // CCW from near-caster left, rounded corners throughout.
  shape.moveTo(-hw + r, 0);
  shape.lineTo(hw - r, 0);
  shape.quadraticCurveTo(hw, 0, hw, r);
  shape.lineTo(hw, L - r);
  shape.quadraticCurveTo(hw, L, hw - r, L);
  shape.lineTo(-hw + r, L);
  shape.quadraticCurveTo(-hw, L, -hw, L - r);
  shape.lineTo(-hw, r);
  shape.quadraticCurveTo(-hw, 0, -hw + r, 0);
  shape.closePath();
  return shape;
}

/** Translucent fill mesh from a ground shape. */
function addAoeFill(root: THREE.Group, shape: THREE.Shape, color: number): void {
  const geo = layFlat(new THREE.ShapeGeometry(shape, 48));
  const mesh = new THREE.Mesh(geo, aoeMat(color, AOE_FILL_OPACITY));
  mesh.position.y = AOE_Y;
  // Draw fill before the solid outline in transparent pass order.
  mesh.renderOrder = 1;
  root.add(mesh);
}

/**
 * Thick outline: a tube that follows the shape perimeter EXACTLY (polyline of
 * LineCurve3 segments). A closed CatmullRom was overshooting past the cone
 * apex and painting a blob behind the caster — never smooth a sharp sector.
 */
function addAoeOutline(root: THREE.Group, shape: THREE.Shape, color: number, radius = AOE_OUTLINE): void {
  const pts2 = shape.getPoints(96);
  if (pts2.length < 3) return;
  // Drop near-duplicate close point if getPoints already closed.
  const first = pts2[0]!;
  const last = pts2[pts2.length - 1]!;
  const closed = first.distanceToSquared(last) < 1e-8;
  const n = closed ? pts2.length - 1 : pts2.length;
  if (n < 3) return;

  const path = new THREE.CurvePath<THREE.Vector3>();
  for (let i = 0; i < n; i++) {
    const a = pts2[i]!;
    const b = pts2[(i + 1) % n]!;
    path.add(new THREE.LineCurve3(
      new THREE.Vector3(a.x, AOE_Y + 0.012, a.y),
      new THREE.Vector3(b.x, AOE_Y + 0.012, b.y),
    ));
  }
  // Path already includes the closing segment — do not also mark closed.
  const segs = Math.max(64, n * 2);
  const geo = new THREE.TubeGeometry(
    path as unknown as THREE.Curve<THREE.Vector3>,
    segs,
    radius,
    5,
    false,
  );
  const mesh = new THREE.Mesh(geo, aoeMat(color)); // outline: fully opaque
  mesh.renderOrder = 2;
  root.add(mesh);
}

function buildSectorAoe(root: THREE.Group, arcDeg: number, range: number, ry: number): void {
  const R = Math.max(range, 0.25);
  const shape = sectorShape(arcDeg, R, AOE_CORNER);
  // Soft blue wash + thin solid white stroke.
  addAoeFill(root, shape, 0x3d9cf0);
  addAoeOutline(root, shape, 0xffffff, AOE_OUTLINE);
  root.rotation.y = ry;
}

function buildLineAoe(root: THREE.Group, length: number, width: number, ry: number): void {
  const shape = lineShape(length, width, AOE_CORNER);
  // Soft orange wash — distinct from sector blue.
  addAoeFill(root, shape, 0xff9a40);
  addAoeOutline(root, shape, 0xffffff, AOE_OUTLINE);
  root.rotation.y = ry;
}

function buildSnare(root: THREE.Group): void {
  // Ground ring of chain-like segments (chunky boxes in a circle).
  const n = 8;
  const r = 0.55;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const box = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.08, 0.28),
      toonMat(0x6b5b3a),
    );
    box.position.set(Math.sin(a) * r, 0.06, Math.cos(a) * r);
    box.rotation.y = a;
    root.add(box);
  }
  // Inner glow disc.
  const disc = new THREE.Mesh(
    new THREE.CylinderGeometry(0.35, 0.35, 0.04, 12),
    toonMat(0xc4a35a),
  );
  disc.position.y = 0.02;
  root.add(disc);
}

function buildSlow(root: THREE.Group): void {
  // Soft cyan ground ripple — reads as "weighted feet", distinct from snare chains.
  const disc = new THREE.Mesh(
    new THREE.CylinderGeometry(0.5, 0.5, 0.03, 16),
    toonMat(0x5ec8e8),
  );
  disc.position.y = 0.02;
  root.add(disc);
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.55, 0.04, 6, 20),
    toonMat(0x2a8fb0),
  );
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.05;
  root.add(ring);
}

function buildStun(root: THREE.Group): void {
  // Orbiting stars above the head.
  const n = 5;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const star = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.12, 0),
      toonMat(0xffe566),
    );
    star.position.set(Math.sin(a) * 0.4, 0.15 + (i % 2) * 0.12, Math.cos(a) * 0.4);
    star.userData.phase = a;
    root.add(star);
  }
  const flash = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 8, 6),
    toonMat(0xfff2a8),
  );
  flash.position.y = 0.2;
  root.add(flash);
}

function buildKnock(root: THREE.Group): void {
  // Impact puff + short directional streak (local +z = shove hint; orient free).
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, 8, 6),
    toonMat(0xff8a5c),
  );
  core.position.y = 0.3;
  root.add(core);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const puff = new THREE.Mesh(
      new THREE.SphereGeometry(0.12, 6, 5),
      toonMat(0xffc29a),
    );
    puff.position.set(Math.sin(a) * 0.28, 0.25, Math.cos(a) * 0.28);
    root.add(puff);
  }
  const streak = new THREE.Mesh(
    new THREE.BoxGeometry(0.12, 0.12, 0.7),
    toonMat(0xffffff),
  );
  streak.position.set(0, 0.35, -0.35);
  root.add(streak);
}

/** Per-frame: age, animate, recycle. Call from the combat update path. */
export function updateCombatFx(dt: number): void {
  for (let i = live.length - 1; i >= 0; i--) {
    const b = live[i]!;
    b.age += dt;
    const t = b.age / b.life;
    if (t >= 1) {
      recycle(i);
      continue;
    }
    // Fade + slight rise / expand.
    const fade = t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85;
    const s = 0.85 + 0.45 * t;
    b.root.scale.setScalar(s);
    if (b.kind === "aoe") {
      // Ground decals stay put; only fade via scale (already applied).
    } else {
      b.root.position.y += dt * (b.kind === "knockback" ? 0.6 : 0.25);
    }
    // Stun stars spin.
    if (b.kind === "stun") {
      for (const c of b.root.children) {
        if (c.userData.phase !== undefined) {
          const phase = (c.userData.phase as number) + b.age * 4;
          c.position.x = Math.sin(phase) * 0.4;
          c.position.z = Math.cos(phase) * 0.4;
          c.rotation.y += dt * 6;
        }
      }
    }
    // Soft hide near end via scale squash (toon mats stay opaque — no material fade).
    if (fade < 0.3) b.root.scale.multiplyScalar(0.5 + fade / 0.3 * 0.5);
  }
}
